import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Assignment } from '../../models/assignment';
import { GroupMemberStateWithUserProgress } from '../../models/group-member-state-with-user-progress';
import { User } from '../../models/user';
import { ExamTakerState } from '../../models/exam-taker-state';
import { ModuleProgress } from '../../models/module-progress';
import { ExamTakerModuleVersion } from '../../models/exam-taker-module-version';
import { ExamTakerPossibleAnswer } from '../../models/exam-taker-possible-answer';
import { sessionService } from '../../services/sessionService';
import { FilePreview } from '../Shared/FilePreview';
import { StaticFileDto } from '../../models/static-file';
import { QuestionResponseOperation } from '../../models/question-response-operation';
import { QuestionType } from '../../models/question-types';
import {
  AssessmentIntegrityEventType,
} from '../../models/assessment-integrity-event';
import { calculateModuleTimeRemaining, formatDateToLocal } from '../../utils/dateUtils';
import { cn } from '../../utils/cn';
import { assessmentIntegrityService } from '../../services/assessmentIntegrityService';
import cssStyles from './Questions.module.css';

interface QuestionsProps {
  // Demo mode props (optional)
  demoMode?: boolean;
  demoModuleVersion?: ExamTakerModuleVersion;
  onDemoComplete?: (answers: Record<string, string[]>) => void;
  onDemoExit?: () => void;
}

interface LocationState {
  moduleProgress: ModuleProgress | null;
  assignment: Assignment;
  groupMember: GroupMemberStateWithUserProgress;
  user: User | ExamTakerState;
}

interface TimeRemainingInfo {
  hasTimeRemaining: boolean;
  isExpired: boolean;
  totalMinutes: number;
  hours: number;
  minutes: number;
  seconds: number;
  remainingSeconds: number;
  formattedTime: string;
  formattedDisplay: string;
}

const placeholderAnswerLabel = /^Option\s+[A-Z]$/i;

const shouldShowAnswerText = (
  answer: ExamTakerPossibleAnswer,
  isVisualAnswerSet: boolean
): boolean => {
  const text = answer.text?.trim();
  return Boolean(text && (!isVisualAnswerSet || !placeholderAnswerLabel.test(text)));
};

const Questions: React.FC<QuestionsProps> = ({ 
  demoMode = false, 
  demoModuleVersion, 
  onDemoComplete,
  onDemoExit 
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const state = location.state as LocationState;

  const [moduleVersion, setModuleVersion] = useState<ExamTakerModuleVersion | null>(null);
  const [moduleProgress, setModuleProgress] = useState<ModuleProgress | null>(state?.moduleProgress || null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [goToInput, setGoToInput] = useState('');
  const [showShakeAnimation, setShowShakeAnimation] = useState(false);
  const [showErrorPopup, setShowErrorPopup] = useState(false);
  const [showGoToModal, setShowGoToModal] = useState(false);
  const [initialAnswers, setInitialAnswers] = useState<Record<string, string[]>>({});
  const [pendingAnswers, setPendingAnswers] = useState<Record<string, string[]>>({});
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState<TimeRemainingInfo | null>(null);
  const [showTimeExpiredModal, setShowTimeExpiredModal] = useState(false);
  const [isTimeBoxHidden, setIsTimeBoxHidden] = useState(false);
  const [freeTextAnswers, setFreeTextAnswers] = useState<Record<string, string>>({});
  const [timerStartTime, setTimerStartTime] = useState<number | null>(null);
  const [initialTimeRemaining, setInitialTimeRemaining] = useState<number | null>(null);
  const [focusLossCount, setFocusLossCount] = useState(0);
  const [focusWarningVisible, setFocusWarningVisible] = useState(false);
  const expiryHandledRef = useRef(false);
  const lastFocusEventRef = useRef(0);
  const focusWarningTimeoutRef = useRef<number | null>(null);
  const integritySequenceRef = useRef(0);
  const integrityAttemptKeyRef = useRef('');
  const integrityOpenedKeyRef = useRef('');
  const previousQuestionIdRef = useRef<string | null>(null);
  const previousQuestionIndexRef = useRef<number | null>(null);
  const questionViewedAtRef = useRef<number | null>(null);
  const navigationAwayRecordedRef = useRef(false);

  const recordIntegrityEvent = useCallback((
    eventType: AssessmentIntegrityEventType,
    options: {
      questionId?: string;
      previousQuestionId?: string;
      reportedElapsedMilliseconds?: number;
      metadata?: Record<string, string>;
    } = {},
    keepalive = false
  ): Promise<void> => {
    if (demoMode || !state?.user?.id || !moduleProgress?.id) {
      return Promise.resolve();
    }

    const attemptKey = `${state.user.id}|${moduleProgress.id}`;
    if (integrityAttemptKeyRef.current !== attemptKey) {
      integrityAttemptKeyRef.current = attemptKey;
      integritySequenceRef.current = 0;
      integrityOpenedKeyRef.current = '';
      previousQuestionIdRef.current = null;
      previousQuestionIndexRef.current = null;
      questionViewedAtRef.current = null;
      navigationAwayRecordedRef.current = false;
    }

    const request = {
      eventType,
      ...options,
      clientOccurredAtUtc: new Date().toISOString(),
      sequenceNumber: ++integritySequenceRef.current,
    };

    if (keepalive) {
      assessmentIntegrityService.recordEventKeepalive(state.user.id, moduleProgress.id, request);
      return Promise.resolve();
    }

    return assessmentIntegrityService.recordEvent(state.user.id, moduleProgress.id, request);
  }, [demoMode, moduleProgress?.id, state?.user?.id]);

  // Helper function to convert string question type to enum
  const parseQuestionType = (typeString: string | number): QuestionType => {
    if (typeof typeString === 'number') {
      return typeString as QuestionType;
    }
    
    switch (typeString) {
      case 'SingleChoice':
        return QuestionType.SingleChoice;
      case 'MultipleChoice':
        return QuestionType.MultipleChoice;
      case 'FreeText':
        return QuestionType.FreeText;
      default:
        return QuestionType.SingleChoice; // Default fallback
    }
  };

  const formatQuestionType = (type: string | number): string => {
    const questionType = parseQuestionType(type);
    switch (questionType) {
      case QuestionType.SingleChoice:
        return 'Single Choice';
      case QuestionType.MultipleChoice:
        return 'Multiple Choice';
      case QuestionType.FreeText:
        return 'Free Text';
      default:
        return 'Single Choice';
    }
  };

  useEffect(() => {
    // In demo mode, skip state validation and use demo data
    if (demoMode) {
      if (demoModuleVersion) {
        setModuleVersion(demoModuleVersion);
        setLoading(false);
      }
      return;
    }

    if (!state || !state.assignment || !state.groupMember || !state.user) {
      navigate('/');
      return;
    }

    loadModuleData();
  }, [demoMode, demoModuleVersion]);

  // Add keyboard event handlers
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Only handle if not typing in an input field
      if (e.target instanceof HTMLInputElement) return;
      
      // Don't allow any navigation if time expired modal is showing
      if (showTimeExpiredModal) return;
      
      switch (e.key) {
        case 'Escape':
          e.preventDefault();
          setShowErrorPopup(false);
          break;
      }
    };

    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest('.go-to-container')) {
        setShowErrorPopup(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('click', handleClickOutside);
    
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('click', handleClickOutside);
    };
  }, [currentQuestionIndex, moduleVersion, showTimeExpiredModal]);

  // Handle ESC key to close Go to Q# modal
  useEffect(() => {
    const handleEscKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && showGoToModal) {
        setShowGoToModal(false);
      }
    };

    window.addEventListener('keydown', handleEscKey);
    return () => window.removeEventListener('keydown', handleEscKey);
  }, [showGoToModal]);

  // State to store demo start time (so it doesn't reset on re-renders)
  const [demoStartTime] = useState<string>(() => new Date().toISOString());

  // Initialize timer from server's timeRemaining
  useEffect(() => {
    if (demoMode || (!moduleVersion?.timeRemaining && !state?.assignment?.allowModuleRetakesUntilAssignmentEnd)) return;
    
    const totalSeconds = state?.assignment?.allowModuleRetakesUntilAssignmentEnd
      ? Math.max(0, Math.floor((new Date(state.assignment.endDateUtc).getTime() - Date.now()) / 1000))
      : moduleVersion!.timeRemaining!.split(':').reduce((total, part) => total * 60 + parseInt(part || '0', 10), 0);
    
    // Initialize timer with server time and current timestamp
    setInitialTimeRemaining(totalSeconds);
    setTimerStartTime(performance.now());
    expiryHandledRef.current = false;
    setShowTimeExpiredModal(false);
  }, [demoMode, moduleVersion?.timeRemaining, state?.assignment?.allowModuleRetakesUntilAssignmentEnd, state?.assignment?.endDateUtc]);

  // Timer logic - update every second when module is active and has a time limit
  useEffect(() => {
    // Check if we have the necessary data for timer
    if (!moduleVersion) {
      return;
    }

    if (!moduleVersion.durationInMinutes && !state?.assignment?.allowModuleRetakesUntilAssignmentEnd) {
      return;
    }

    const updateTimer = () => {
      let timeInfo: TimeRemainingInfo;
      
      if (demoMode) {
        // For demo mode, calculate client-side
        timeInfo = calculateModuleTimeRemaining(
          demoStartTime,
          moduleVersion.durationInMinutes
        );
      } else if (timerStartTime && initialTimeRemaining !== null) {
        // Calculate remaining time based on initial server time and elapsed client time
        // A monotonic clock keeps system-clock changes from pausing or extending the timer.
        const elapsedSeconds = Math.floor((performance.now() - timerStartTime) / 1000);
        const remainingSeconds = Math.max(0, initialTimeRemaining - elapsedSeconds);
        
        const hours = Math.floor(remainingSeconds / 3600);
        const minutes = Math.floor((remainingSeconds % 3600) / 60);
        const seconds = remainingSeconds % 60;
        const expired = remainingSeconds <= 0;
        const totalMinutes = expired ? 0 : Math.ceil(remainingSeconds / 60);
        const formattedTime = hours > 0
          ? `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
          : `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
        
        timeInfo = {
          hasTimeRemaining: !expired,
          isExpired: expired,
          totalMinutes,
          hours,
          minutes,
          seconds,
          remainingSeconds,
          formattedTime,
          formattedDisplay: expired 
            ? 'Time Expired'
            : (hours > 0
              ? `${hours}h ${minutes}m remaining`
              : remainingSeconds < 60
                ? `${seconds}s remaining`
                : `${minutes}m ${seconds}s remaining`)
        };
      } else {
        return;
      }
      
      setTimeRemaining(timeInfo);
      
      // Check if time just expired and save the active question before showing the modal.
      if (timeInfo.isExpired && !expiryHandledRef.current) {
        expiryHandledRef.current = true;
        setShowTimeExpiredModal(true);
        setShowConfirmationModal(false);
        const expiredQuestion = moduleVersion.questions[currentQuestionIndex];
        if (expiredQuestion) {
          void submitPendingAnswerForQuestion(expiredQuestion.id);
        }
      }
    };

    // Update immediately
    updateTimer();

    // Update every second
    const interval = setInterval(updateTimer, 1000);

    return () => clearInterval(interval);
  }, [demoMode, demoStartTime, timerStartTime, initialTimeRemaining, currentQuestionIndex, moduleVersion, state?.assignment?.allowModuleRetakesUntilAssignmentEnd]);

  // Warn when the assessment window loses focus. This is a lightweight integrity signal;
  // it does not interrupt the attempt or claim to prevent second-device assistance.
  useEffect(() => {
    if (demoMode || !moduleProgress?.id) return;

    const registerFocusLoss = () => {
      if (timeRemaining?.isExpired) return;

      const now = Date.now();
      if (now - lastFocusEventRef.current < 1200) return;
      lastFocusEventRef.current = now;
      setFocusLossCount(previousCount => previousCount + 1);
      setFocusWarningVisible(true);
      void recordIntegrityEvent('FocusLost', {
        metadata: {
          visibilityState: document.visibilityState,
          focusLossCount: String(focusLossCount + 1),
        },
      });

      if (focusWarningTimeoutRef.current !== null) {
        window.clearTimeout(focusWarningTimeoutRef.current);
      }

      focusWarningTimeoutRef.current = window.setTimeout(() => {
        setFocusWarningVisible(false);
        focusWarningTimeoutRef.current = null;
      }, 6000);
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        registerFocusLoss();
      } else if (!timeRemaining?.isExpired) {
        void recordIntegrityEvent('FocusGained', {
          metadata: { visibilityState: document.visibilityState },
        });
      }
    };

    const handleWindowBlur = () => {
      if (!document.hidden) registerFocusLoss();
    };

    const handleWindowFocus = () => {
      if (!document.hidden && !timeRemaining?.isExpired) {
        void recordIntegrityEvent('FocusGained', {
          metadata: { visibilityState: document.visibilityState },
        });
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('blur', handleWindowBlur);
    window.addEventListener('focus', handleWindowFocus);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('blur', handleWindowBlur);
      window.removeEventListener('focus', handleWindowFocus);
      if (focusWarningTimeoutRef.current !== null) {
        window.clearTimeout(focusWarningTimeoutRef.current);
        focusWarningTimeoutRef.current = null;
      }
    };
  }, [demoMode, focusLossCount, moduleProgress?.id, recordIntegrityEvent, timeRemaining?.isExpired]);

  // Record the initial load separately from a browser reload so administrators can
  // distinguish an ordinary visit from repeated restarts of the same attempt.
  useEffect(() => {
    if (demoMode || !moduleProgress?.id || !moduleVersion) return;

    const attemptKey = `${moduleProgress.id}|${moduleVersion.id}`;
    if (integrityOpenedKeyRef.current === attemptKey) return;
    integrityOpenedKeyRef.current = attemptKey;

    const navigationEntry = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined;
    const navigationType = navigationEntry?.type || 'unknown';
    const eventType: AssessmentIntegrityEventType = navigationType === 'reload'
      ? 'AssessmentReloaded'
      : 'AssessmentOpened';

    void recordIntegrityEvent(eventType, {
      metadata: { navigationType },
    });
  }, [demoMode, moduleProgress?.id, moduleVersion?.id, recordIntegrityEvent]);

  // Question views and transitions provide the server with a timing anchor for
  // answer submissions and a durable navigation trail.
  useEffect(() => {
    if (demoMode || !moduleProgress?.id || !moduleVersion?.questions) return;

    const question = moduleVersion.questions[currentQuestionIndex];
    if (!question) return;

    const viewedAt = performance.now();
    const previousQuestionId = previousQuestionIdRef.current;
    const previousQuestionIndex = previousQuestionIndexRef.current;
    const elapsedMilliseconds = questionViewedAtRef.current === null
      ? undefined
      : Math.max(0, Math.round(viewedAt - questionViewedAtRef.current));

    if (previousQuestionId && previousQuestionId !== question.id) {
      void recordIntegrityEvent('QuestionNavigated', {
        questionId: question.id,
        previousQuestionId,
        reportedElapsedMilliseconds: elapsedMilliseconds,
        metadata: {
          fromPosition: String((previousQuestionIndex ?? currentQuestionIndex) + 1),
          toPosition: String(currentQuestionIndex + 1),
          direction: previousQuestionIndex !== null && currentQuestionIndex > previousQuestionIndex
            ? 'forward'
            : 'backward',
        },
      });
    }

    void recordIntegrityEvent('QuestionViewed', {
      questionId: question.id,
    });

    previousQuestionIdRef.current = question.id;
    previousQuestionIndexRef.current = currentQuestionIndex;
    questionViewedAtRef.current = viewedAt;
  }, [currentQuestionIndex, demoMode, moduleProgress?.id, moduleVersion?.id, moduleVersion?.questions, recordIntegrityEvent]);

  // pagehide is the last reliable opportunity to record a tab close, reload, or
  // route change. sendBeacon/fetch keepalive allows the request to outlive unload.
  useEffect(() => {
    if (demoMode || !moduleProgress?.id) return;

    const handlePageHide = (event: PageTransitionEvent) => {
      if (navigationAwayRecordedRef.current) return;
      navigationAwayRecordedRef.current = true;
      recordIntegrityEvent('NavigationAway', {
        metadata: { persisted: String(event.persisted) },
      }, true);
    };

    const handleHistoryNavigation = () => {
      if (navigationAwayRecordedRef.current) return;
      navigationAwayRecordedRef.current = true;
      recordIntegrityEvent('NavigationAway', {
        metadata: { reason: 'browser-history' },
      }, true);
    };

    window.addEventListener('pagehide', handlePageHide);
    window.addEventListener('popstate', handleHistoryNavigation);
    return () => {
      window.removeEventListener('pagehide', handlePageHide);
      window.removeEventListener('popstate', handleHistoryNavigation);
    };
  }, [demoMode, moduleProgress?.id, recordIntegrityEvent]);

  const loadModuleData = async () => {
    if (!state || !state.assignment || !state.groupMember || !state.user) {
      setError('Missing required data to load module');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError('');
    
    try {
      // If we don't have module progress yet, we need to get it to get the correct version ID
      let currentModuleProgress = moduleProgress;
      
      if (!currentModuleProgress) {
        // Get module progress to get the correct version ID
        const progressResponse = await sessionService.getModuleProgress(
          state.user.id,
          state.assignment.id,
          state.groupMember.assessmentModuleId
        );

        if (progressResponse.isSuccess && progressResponse.data) {
          currentModuleProgress = progressResponse.data;
          setModuleProgress(currentModuleProgress);
        } else {
          setError('Failed to create module progress: ' + (progressResponse.message || 'Unknown error'));
          return;
        }
      }

      // Now get the module version for the exam taker using the version ID from progress
      const versionId = currentModuleProgress.assessmentModuleVersionId;
      
      const moduleVersionResponse = await sessionService.getModuleVersionForExamTaker(
        state.user.id,
        state.assignment.id,
        versionId
      );

      if (moduleVersionResponse.isSuccess && moduleVersionResponse.data) {
        setModuleVersion(moduleVersionResponse.data);
      } else {
        setError('Failed to load module data: ' + (moduleVersionResponse.message || 'Unknown error'));
      }
    } catch (err: any) {
      setError('Failed to load questions: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleBack = async () => {
    // In demo mode, call the exit callback
    if (demoMode && onDemoExit) {
      onDemoExit();
      return;
    }

    // Submit pending answer for current question before leaving
    const currentQuestion = moduleVersion?.questions[currentQuestionIndex];
    if (currentQuestion) {
      await submitPendingAnswerForQuestion(currentQuestion.id);
    }

    if (!navigationAwayRecordedRef.current) {
      navigationAwayRecordedRef.current = true;
      await recordIntegrityEvent('NavigationAway', {
        metadata: { reason: 'back-button' },
      });
    }
    navigate(-1);
  };

  const getCurrentQuestion = () => {
    if (!moduleVersion?.questions || currentQuestionIndex >= moduleVersion.questions.length) {
      return null;
    }
    return moduleVersion.questions[currentQuestionIndex];
  };

  const handleNextQuestion = async () => {
    // Don't allow navigation if time has expired
    if (timeRemaining?.isExpired) return;
    
    if (moduleVersion?.questions && currentQuestionIndex < moduleVersion.questions.length - 1) {
      // Submit pending answer for current question before navigating
      const currentQuestion = moduleVersion.questions[currentQuestionIndex];
      if (currentQuestion) {
        await submitPendingAnswerForQuestion(currentQuestion.id);
      }
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    }
  };

  const handlePreviousQuestion = async () => {
    // Don't allow navigation if time has expired
    if (timeRemaining?.isExpired) return;
    
    if (currentQuestionIndex > 0) {
      // Submit pending answer for current question before navigating
      const currentQuestion = moduleVersion?.questions[currentQuestionIndex];
      if (currentQuestion) {
        await submitPendingAnswerForQuestion(currentQuestion.id);
      }
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
  };

  const handleGoToQuestion = async (questionNumber: number) => {
    // Don't allow navigation if time has expired
    if (timeRemaining?.isExpired) return;
    
    if (moduleVersion?.questions && questionNumber >= 1 && questionNumber <= moduleVersion.questions.length) {
      // Submit pending answer for current question before navigating
      const currentQuestion = moduleVersion.questions[currentQuestionIndex];
      if (currentQuestion) {
        await submitPendingAnswerForQuestion(currentQuestion.id);
      }
      setCurrentQuestionIndex(questionNumber - 1);
      setGoToInput('');
      setShowErrorPopup(false);
      setShowGoToModal(false); // Close modal after navigation
    } else {
      triggerShakeAndError();
    }
  };

  const triggerShakeAndError = () => {
    // Trigger shake animation and show error popup
    setShowShakeAnimation(true);
    setShowErrorPopup(true);
    
    // Create a more pronounced shake effect
    const element = document.querySelector('.go-to-container') as HTMLElement;
    if (element) {
      element.style.animation = 'none';
      void element.offsetHeight; // Trigger reflow
      element.style.animation = 'shake 0.5s ease-in-out';
    }
    
    // Reset shake animation after it completes
    setTimeout(() => {
      setShowShakeAnimation(false);
      if (element) {
        element.style.animation = '';
      }
    }, 500);
    
    // Hide error popup after 3 seconds
    setTimeout(() => {
      setShowErrorPopup(false);
    }, 3000);
  };

  const handleGoToInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // Only allow numeric input
    if (value === '' || /^\d+$/.test(value)) {
      setGoToInput(value);
      
      // Check if the entered number is invalid and trigger shake
      if (value !== '' && moduleVersion?.questions) {
        const questionNumber = parseInt(value);
        if (!isNaN(questionNumber) && (questionNumber < 1 || questionNumber > moduleVersion.questions.length)) {
          triggerShakeAndError();
        } else {
          // Hide error popup if valid number is entered
          setShowErrorPopup(false);
        }
      }
    }
  };

  const handleGoToKeyPress = async (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      const questionNumber = parseInt(goToInput);
      if (!isNaN(questionNumber)) {
        await handleGoToQuestion(questionNumber);
      }
    }
  };

  const handleGoToSubmit = async () => {
    const questionNumber = parseInt(goToInput);
    if (!isNaN(questionNumber)) {
      await handleGoToQuestion(questionNumber);
    }
  };

  // Helper function to convert staticFileUrls to StaticFileDto format for FilePreview
  const convertUrlsToStaticFiles = (urls: string[] | undefined): StaticFileDto[] => {
    if (!urls || urls.length === 0) return [];
    
    return urls.map((url, index) => {
      const fileName = url.split('/').pop() || `file-${index + 1}`;
      const extension = fileName.split('.').pop() || '';
      
      return {
        id: `static-${index}`,
        name: fileName,
        url: url,
        type: getFileTypeFromExtension(extension)
      };
    });
  };

  // Helper function to determine file type from extension
  const getFileTypeFromExtension = (extension: string): string => {
    const ext = extension.toLowerCase();
    if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(ext)) return 'image/' + ext;
    if (['mp4', 'webm', 'mov', 'avi'].includes(ext)) return 'video/' + ext;
    if (['mp3', 'wav', 'ogg', 'aac'].includes(ext)) return 'audio/' + ext;
    if (ext === 'pdf') return 'application/pdf';
    return 'application/octet-stream';
  };



  // Helper function to get the current question's response from module progress
  const getCurrentQuestionResponse = () => {
    if (!moduleProgress?.questionResponses || !moduleVersion) {
      return null;
    }
    
    const currentQuestion = getCurrentQuestion();
    if (!currentQuestion) return null;
    
    return moduleProgress.questionResponses.find(
      response => response.questionId === currentQuestion.id
    );
  };

  const getCurrentQuestionElapsedMilliseconds = (): number | undefined => {
    if (questionViewedAtRef.current === null) return undefined;
    return Math.max(0, Math.round(performance.now() - questionViewedAtRef.current));
  };

  // Helper function to check if an answer is selected based on pending answers or progress
  const isAnswerSelected = (answerId: string): boolean => {
    const currentQuestion = moduleVersion?.questions[currentQuestionIndex];
    if (!currentQuestion) return false;
    
    // Check pending answers first (user's current selections)
    const pendingAnswer = pendingAnswers[currentQuestion.id];
    if (pendingAnswer) {
      return pendingAnswer.includes(answerId);
    }
    
    // Fall back to saved progress if no pending changes
    const response = getCurrentQuestionResponse();
    if (!response?.selectedAnswerIds) {
      return false;
    }
    
    return response.selectedAnswerIds.includes(answerId);
  };

  // Function to handle free text input changes
  const handleFreeTextChange = (questionId: string, value: string) => {
    // Don't allow changes if time has expired
    if (timeRemaining?.isExpired) return;
    
    setFreeTextAnswers(prev => ({
      ...prev,
      [questionId]: value
    }));
  };

  // Function to handle answer selection (store locally, don't submit yet)
  const handleAnswerSelection = (answerId: string, questionId: string, questionType: QuestionType) => {
    // Don't allow answer changes if time has expired
    if (timeRemaining?.isExpired) return;
    
    const currentlySelected = pendingAnswers[questionId] || [];
    let newSelectedAnswerIds: string[];
    
    // Handle different question types
    if (questionType === QuestionType.SingleChoice) {
      // For single choice, replace the selection
      newSelectedAnswerIds = [answerId];
    } else if (questionType === QuestionType.MultipleChoice) {
      // For multiple choice, toggle the selection
      if (currentlySelected.includes(answerId)) {
        newSelectedAnswerIds = currentlySelected.filter(id => id !== answerId);
      } else {
        newSelectedAnswerIds = [...currentlySelected, answerId];
      }
    } else {
      // For other types, default to single choice behavior
      newSelectedAnswerIds = [answerId];
    }
    
    // Update pending answers
    setPendingAnswers(prev => ({
      ...prev,
      [questionId]: newSelectedAnswerIds
    }));
  };

  // Function to submit pending answers for the current question
  const submitPendingAnswerForQuestion = async (questionId: string) => {
    // In demo mode, just move pending answers to initial answers (mark as submitted)
    if (demoMode) {
      const pendingAnswer = pendingAnswers[questionId];
      if (pendingAnswer && pendingAnswer.length > 0) {
        setInitialAnswers(prev => ({
          ...prev,
          [questionId]: [...pendingAnswer]
        }));
      }
      return;
    }

    if (!moduleProgress?.id) {
      return;
    }

    const question = moduleVersion?.questions.find(q => q.id === questionId);
    if (!question) {
      return;
    }

    let pendingAnswer: string[];
    let initialAnswer: string[] = initialAnswers[questionId] || [];

    // Handle free text questions differently
    if (parseQuestionType(question.type) === QuestionType.FreeText) {
      const freeTextValue = freeTextAnswers[questionId];
      if (!freeTextValue || freeTextValue.trim() === '') {
        return; // No answer to submit
      }
      
      // Check if the free text content has actually changed
      const existingResponse = moduleProgress.questionResponses?.find(r => r.questionId === questionId);
      const existingTextResponse = existingResponse?.textResponse || '';
      
      if (freeTextValue.trim() === existingTextResponse.trim()) {
        return; // No changes to submit - text is the same
      }
      
      // For free text, we submit the text value, but need to match it with answer IDs
      // The backend will handle matching the text to acceptable answers
      pendingAnswer = [freeTextValue]; // Store the text value temporarily
    } else {
      pendingAnswer = pendingAnswers[questionId];
      // Check if there are pending changes for this question
      if (!pendingAnswer || JSON.stringify([...pendingAnswer].sort()) === JSON.stringify([...initialAnswer].sort())) {
        return; // No changes to submit
      }
    }

    try {
      const questionResponse: QuestionResponseOperation = {
        questionId: questionId,
        selectedAnswerIds: parseQuestionType(question.type) === QuestionType.FreeText ? [] : pendingAnswer, // Empty for free text
        textResponse: parseQuestionType(question.type) === QuestionType.FreeText ? freeTextAnswers[questionId] : undefined, // For FreeText
        questionType: question.type,
        respondedAtUtc: new Date().toISOString()
      };

      const response = await sessionService.submitAnswer(moduleProgress.id, questionResponse);

      if (response.isSuccess) {
        void recordIntegrityEvent('AnswerSubmitted', {
          questionId,
          reportedElapsedMilliseconds: getCurrentQuestionElapsedMilliseconds(),
          metadata: {
            questionType: String(question.type),
            answerCount: String(parseQuestionType(question.type) === QuestionType.FreeText
              ? 1
              : pendingAnswer.length),
          },
        });
      }
      
      // Update the module progress state to reflect the change
      setModuleProgress(prev => {
        if (!prev) return prev;
        
        const updatedResponses = prev.questionResponses ? [...prev.questionResponses] : [];
        const existingResponseIndex = updatedResponses.findIndex(r => r.questionId === questionId);
        
        const isFreetextQuestion = parseQuestionType(question.type) === QuestionType.FreeText;
        
        if (existingResponseIndex >= 0) {
          // Update existing response
          updatedResponses[existingResponseIndex] = {
            ...updatedResponses[existingResponseIndex],
            selectedAnswerIds: isFreetextQuestion ? [] : pendingAnswer,
            textResponse: isFreetextQuestion ? freeTextAnswers[questionId] : updatedResponses[existingResponseIndex].textResponse,
            respondedAtUtc: new Date().toISOString()
          };
        } else {
          // Add new response
          updatedResponses.push({
            id: '', // Will be set by backend
            questionId: questionId,
            selectedAnswerIds: isFreetextQuestion ? [] : pendingAnswer,
            textResponse: isFreetextQuestion ? freeTextAnswers[questionId] : undefined,
            questionType: question.type === QuestionType.SingleChoice ? 'SingleChoice' :
                          question.type === QuestionType.MultipleChoice ? 'MultipleChoice' : 
                          'FreeText',
            respondedAtUtc: new Date().toISOString(),
            moduleProgressId: prev.id
          });
        }
        
        return {
          ...prev,
          questionResponses: updatedResponses
        };
      });
      
      // Update initial answers to reflect the new submitted state
      if (parseQuestionType(question.type) === QuestionType.FreeText) {
        // For free text questions, we don't store the text in initialAnswers
        // The text is already stored in the moduleProgress.textResponse above
      } else {
        setInitialAnswers(prev => ({
          ...prev,
          [questionId]: [...pendingAnswer]
        }));
      }
      
    } catch (error) {
      // Error submitting answer
    }
  };

  // Function to get list of unanswered questions
  const getUnansweredQuestions = () => {
    if (!moduleVersion?.questions) return [];
    
    return moduleVersion.questions.filter(question => {
      const selectedAnswers = pendingAnswers[question.id] ?? initialAnswers[question.id] ?? [];
      const isAnswered = selectedAnswers.length > 0 ||
                        (freeTextAnswers[question.id] && freeTextAnswers[question.id].trim().length > 0);
      return !isAnswered;
    });
  };

  // Function to handle module submit button click (shows confirmation modal)
  const handleSubmitModule = async () => {
    if (timeRemaining?.isExpired) return;

    // Submit pending answer for current question before showing confirmation
    const currentQuestion = moduleVersion?.questions[currentQuestionIndex];
    if (currentQuestion) {
      await submitPendingAnswerForQuestion(currentQuestion.id);
    }
    
    setShowConfirmationModal(true);
  };

  // Function to handle confirmed module submission
  const handleConfirmSubmit = async () => {
    // In demo mode, call the completion callback with all answers
    if (demoMode) {
      setIsSubmitting(true);
      try {
        // Collect all answers (both pending and free text)
        const allAnswers = { ...pendingAnswers };
        
        // Add free text answers
        Object.keys(freeTextAnswers).forEach(questionId => {
          if (freeTextAnswers[questionId]) {
            allAnswers[questionId] = [freeTextAnswers[questionId]];
          }
        });
        
        // Store in localStorage for demo scoring
        localStorage.setItem('demo_user_answers', JSON.stringify(allAnswers));
        
        if (onDemoComplete) {
          onDemoComplete(allAnswers);
        }
      } finally {
        setIsSubmitting(false);
        setShowConfirmationModal(false);
      }
      return;
    }

    if (!moduleProgress) {
      return;
    }

    setIsSubmitting(true);
    try {
      await recordIntegrityEvent('AssessmentSubmitted', {
        metadata: {
          answeredQuestions: String(moduleVersion?.questions.filter(question => {
            const selectedAnswers = pendingAnswers[question.id] ?? initialAnswers[question.id] ?? [];
            return selectedAnswers.length > 0 || Boolean(freeTextAnswers[question.id]?.trim());
          }).length ?? 0),
          totalQuestions: String(moduleVersion?.questions.length ?? 0),
        },
      });
      const response = await sessionService.completeModule(moduleProgress.id);
      
      if (response.isSuccess) {
        // Store completion info in localStorage for celebration animation
        if (state && state.groupMember) {
          const completionInfo = {
            moduleId: state.groupMember.id,
            completedAt: new Date().toISOString(),
            assignmentId: state.assignment.id
          };
          localStorage.setItem('moduleCompletion', JSON.stringify(completionInfo));
        }
        
        // Navigate back normally
        navigate(-1);
      } else {
        setError('Failed to complete module: ' + (response.message || 'Unknown error'));
        setShowConfirmationModal(false);
      }
    } catch (err: any) {
      setError('Failed to complete module: ' + (err.response?.data?.message || err.message));
      setShowConfirmationModal(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Function to cancel module submission
  const handleCancelSubmit = () => {
    setShowConfirmationModal(false);
  };

  // Function to handle time expired - redirect back to assignment execution
  const handleTimeExpired = async () => {
    // In demo mode, just exit
    if (demoMode && onDemoExit) {
      onDemoExit();
      return;
    }

    // Submit pending answer for current question before leaving
    const currentQuestion = moduleVersion?.questions[currentQuestionIndex];
    if (currentQuestion) {
      await submitPendingAnswerForQuestion(currentQuestion.id);
    }

    if (!navigationAwayRecordedRef.current) {
      navigationAwayRecordedRef.current = true;
      await recordIntegrityEvent('NavigationAway', {
        metadata: { reason: 'time-expired' },
      });
    }
    
    // Navigate back to assignment execution
    navigate(-1);
  };

  // Function to initialize the initial answers and pending answers when module progress loads
  const initializeAnswers = useCallback(() => {
    if (!moduleProgress?.questionResponses || !moduleVersion?.questions) {
      return;
    }
    
    const initial: Record<string, string[]> = {};
    const pending: Record<string, string[]> = {};
    const freeTextAnswers: Record<string, string> = {};
    
    moduleProgress.questionResponses.forEach(response => {
      if (response.selectedAnswerIds) {
        initial[response.questionId] = [...response.selectedAnswerIds];
        pending[response.questionId] = [...response.selectedAnswerIds];
      }
      if (response.textResponse) {
        freeTextAnswers[response.questionId] = response.textResponse;
      }
    });
    
    setInitialAnswers(initial);
    setPendingAnswers(pending);
    setFreeTextAnswers(freeTextAnswers);
  }, [moduleProgress, moduleVersion]);

  // Initialize answers when module progress or version changes
  useEffect(() => {
    initializeAnswers();
  }, [initializeAnswers]);

  // Prevent text copying via keyboard shortcuts and context menu
  useEffect(() => {
    const preventCopy = (e: KeyboardEvent) => {
      // Prevent Ctrl+C, Ctrl+A, Ctrl+X, Ctrl+V, Ctrl+S, F12, etc.
      if ((e.ctrlKey || e.metaKey) && (
        e.key === 'c' || 
        e.key === 'a' || 
        e.key === 'x' || 
        e.key === 'v' || 
        e.key === 's' ||
        e.key === 'p'
      )) {
        e.preventDefault();
        e.stopPropagation();
        return false;
      }
      
      // Prevent F12 (Developer Tools)
      if (e.key === 'F12') {
        e.preventDefault();
        e.stopPropagation();
        return false;
      }
      
      // Prevent Ctrl+Shift+I (Developer Tools)
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 'I') {
        e.preventDefault();
        e.stopPropagation();
        return false;
      }
    };

    const preventContextMenu = (e: MouseEvent) => {
      e.preventDefault();
      return false;
    };

    // Add event listeners
    document.addEventListener('keydown', preventCopy, true);
    document.addEventListener('contextmenu', preventContextMenu, true);

    return () => {
      // Clean up event listeners
      document.removeEventListener('keydown', preventCopy, true);
      document.removeEventListener('contextmenu', preventContextMenu, true);
    };
  }, []);

  const currentQuestion = getCurrentQuestion();

  // Validate that we have required state before rendering (skip in demo mode)
  if (!demoMode && (!state || !state.assignment || !state.groupMember || !state.user)) {
    return (
      <div className={cssStyles.container}>
        <div className={cssStyles.header}>
          <button onClick={() => navigate('/')} className={cssStyles.backButton}>
            ← Back
          </button>
        </div>
        <div className={cssStyles.errorContainer}>
          <h3>Error</h3>
          <p>Missing required data. Please try accessing this module from the assignment page.</p>
          <button onClick={() => navigate('/')} className={cssStyles.retryButton}>
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className={cssStyles.container}>
        <div className={cssStyles.header}>
          <button onClick={handleBack} className={cssStyles.backButton}>
            ← Back
          </button>
        </div>
        <div className={cssStyles.loadingContainer}>
          <div className={cssStyles.spinner}></div>
          <p>Loading questions...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={cssStyles.container}>
        <div className={cssStyles.header}>
          <button onClick={handleBack} className={cssStyles.backButton}>
            ← Back
          </button>
        </div>
        <div className={cssStyles.errorContainer}>
          <h3>Error</h3>
          <p>{error}</p>
          <button onClick={loadModuleData} className={cssStyles.retryButton}>
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (!moduleVersion || !currentQuestion) {
    return (
      <div className={cssStyles.container}>
        <div className={cssStyles.header}>
          <button onClick={handleBack} className={cssStyles.backButton}>
            ← Back
          </button>
        </div>
        <div className={cssStyles.errorContainer}>
          <h3>No Questions Available</h3>
          <p>This module does not contain any questions.</p>
        </div>
      </div>
    );
  }

  const isVisualAnswerSet = currentQuestion.answers.length > 1 &&
    currentQuestion.answers.every(answer => answer.staticFileUrls && answer.staticFileUrls.length > 0);
  const answeredQuestionCount = moduleVersion.questions.filter(question => {
    const selectedAnswers = pendingAnswers[question.id] ?? initialAnswers[question.id] ?? [];
    return selectedAnswers.length > 0 || Boolean(freeTextAnswers[question.id]?.trim());
  }).length;

  return (
    <div className={cn(cssStyles.container, "questions-container")}>
      <style>
        {`
          @keyframes shake {
            0%, 100% { transform: translateX(0); }
            25% { transform: translateX(-3px); }
            75% { transform: translateX(3px); }
          }
          
          /* Mobile responsive styles for Questions component */
          @media (max-width: 768px) {
            .questions-container {
              padding: 12px !important;
              overflow-x: auto !important;
            }
            
            .questions-header {
              flex-direction: row !important;
              flex-wrap: wrap !important;
              gap: 8px !important;
              margin-bottom: 16px !important;
              align-items: center !important;
            }
            
            .questions-header-info {
              flex: 1 1 0 !important;
              min-width: 0 !important;
              overflow: hidden !important;
            }
            
            .questions-header-info h2 {
              font-size: 18px !important;
              margin: 0 0 4px 0 !important;
              white-space: nowrap !important;
              overflow: hidden !important;
              text-overflow: ellipsis !important;
            }
            
            .questions-header-info p {
              font-size: 13px !important;
              margin: 0 !important;
              white-space: nowrap !important;
              overflow: hidden !important;
              text-overflow: ellipsis !important;
            }
            
            .questions-back-button {
              padding: 8px 16px !important;
              font-size: 14px !important;
              width: fit-content !important;
              order: -1 !important;
              flex-basis: 100% !important;
            }
            
            .questions-time-remaining-box {
              position: static !important;
              margin: 0 !important;
              width: fit-content !important;
              flex-shrink: 0 !important;
            }
            
            .questions-progress-bar {
              margin-bottom: 20px !important;
            }
            
            .questions-question-container {
              padding: 16px !important;
              margin-bottom: 16px !important;
            }
            
            .questions-question-header {
              margin-bottom: 16px !important;
            }
            
            .questions-question-header h3 {
              font-size: 16px !important;
              margin: 0 !important;
              white-space: nowrap !important;
            }
            
            .questions-question-text {
              font-size: 16px !important;
              margin-bottom: 20px !important;
            }
            
            .questions-question-attachments {
              margin-bottom: 16px !important;
            }
            
            .questions-answers-container {
              gap: 12px !important;
              overflow-x: auto !important;
            }
            
            .questions-answer-option {
              padding: 12px !important;
              flex-direction: column !important;
              align-items: flex-start !important;
              gap: 8px !important;
            }
            
            .questions-answer-label {
              font-size: 14px !important;
            }
            
            .questions-navigation {
              position: sticky !important;
              bottom: 0 !important;
              padding: 16px !important;
            }
            
            .questions-navigation-controls {
              flex-direction: column !important;
              gap: 16px !important;
            }
            
            .questions-main-buttons {
              display: flex !important;
              flex-direction: row !important;
              gap: 8px !important;
              width: 100% !important;
              justify-content: space-between !important;
            }
            
            .questions-navigation-button,
            .questions-submit-button {
              flex: 1 !important;
              padding: 10px 8px !important;
              font-size: 13px !important;
              min-width: 0 !important;
              white-space: nowrap !important;
            }
            
            .questions-go-to-container {
              flex-direction: column !important;
              gap: 8px !important;
              width: 100% !important;
            }
            
            .questions-go-to-input {
              width: 100% !important;
              padding: 10px !important;
            }
            
            .questions-go-to-button {
              width: 100% !important;
              padding: 10px !important;
            }
            
            .questions-modal {
              width: 95% !important;
              max-width: 95% !important;
              max-height: 90% !important;
              margin: 20px auto !important;
            }
          }
          
          @media (max-width: 480px) {
            .questions-container {
              padding: 8px !important;
            }
            
            .questions-question-container {
              padding: 12px !important;
            }
            
            .questions-header-info h2 {
              font-size: 18px !important;
            }
            
            .questions-question-text {
              font-size: 14px !important;
            }
            
            .questions-answer-label {
              font-size: 13px !important;
            }
            
            .questions-modal {
              width: 98% !important;
              margin: 10px auto !important;
            }
          }
          
          /* Super narrow screens - make scrollable */
          @media (max-width: 320px) {
            .questions-container {
              padding: 4px !important;
              min-height: 100vh !important;
              overflow-x: auto !important;
              min-width: 280px !important;
            }
            
            .questions-header {
              flex-wrap: wrap !important;
              gap: 8px !important;
              margin-bottom: 12px !important;
              min-width: 280px !important;
            }
            
            .questions-header-info {
              min-width: 240px !important;
            }
            
            .questions-header-info h2 {
              font-size: 16px !important;
              line-height: 1.2 !important;
              word-break: break-word !important;
            }
            
            .questions-header-info p {
              font-size: 12px !important;
              line-height: 1.3 !important;
              word-break: break-word !important;
            }
            
            .questions-question-container {
              padding: 8px !important;
              min-width: 270px !important;
              overflow-x: auto !important;
            }
            
            .questions-question-header h3 {
              font-size: 16px !important;
              word-break: break-word !important;
            }
            
            .questions-question-text {
              font-size: 13px !important;
              line-height: 1.4 !important;
              word-break: break-word !important;
              overflow-wrap: break-word !important;
              margin-bottom: 12px !important;
              min-width: 260px !important;
            }
            
            .questions-question-attachments {
              margin-bottom: 12px !important;
              min-width: 260px !important;
            }
            
            .questions-answers-container {
              min-width: 260px !important;
              overflow-x: auto !important;
            }
            
            .questions-answer-option {
              padding: 8px !important;
              min-width: 250px !important;
              word-break: break-word !important;
            }
            
            .questions-answer-label {
              font-size: 12px !important;
              line-height: 1.3 !important;
              word-break: break-word !important;
            }
            
            .questions-navigation {
              padding: 8px !important;
              min-width: 270px !important;
              overflow-x: auto !important;
            }
            
            .questions-navigation-button,
            .questions-submit-button {
              flex: 1 !important;
              padding: 8px 6px !important;
              font-size: 11px !important;
              min-height: 36px !important;
              min-width: 0 !important;
              white-space: nowrap !important;
              overflow: hidden !important;
              text-overflow: ellipsis !important;
            }
            
            .questions-main-buttons {
              display: flex !important;
              flex-direction: row !important;
              gap: 4px !important;
              width: 100% !important;
            }
            
            .questions-go-to-container {
              min-width: 250px !important;
            }
            
            .questions-modal {
              width: 99% !important;
              margin: 5px auto !important;
              max-height: 95vh !important;
              overflow-y: auto !important;
            }
            
            .questions-back-button {
              padding: 6px 12px !important;
              font-size: 12px !important;
              min-width: 60px !important;
            }
            
            .questions-time-remaining-box {
              transform: scale(0.9) !important;
            }
          }
        `}
      </style>
      <div className={cn(cssStyles.header, "questions-header")}>
        <button onClick={handleBack} className={cn(cssStyles.backButton, "questions-back-button")}>
          ← Back
        </button>
        <div className={cn(cssStyles.headerInfo, "questions-header-info")}>
          {demoMode ? (
            <>
              <h2>Demo Assessment</h2>
              <p>{moduleVersion.title}</p>
            </>
          ) : (
            <>
              <h2>{state.assignment.title}</h2>
              <p>{state.groupMember.assessmentModuleTitle}</p>
            </>
          )}
        </div>
        {timeRemaining && (
          <div 
            className={cn(
              cssStyles.timeRemainingBox,
              "questions-time-remaining-box",
              timeRemaining.isExpired && cssStyles['timeRemainingBox--expired'],
              !timeRemaining.isExpired && timeRemaining.remainingSeconds <= 60 && cssStyles['timeRemainingBox--critical'],
              !timeRemaining.isExpired && timeRemaining.remainingSeconds <= 300 && timeRemaining.remainingSeconds > 60 && cssStyles['timeRemainingBox--urgent']
            )}
            onClick={() => {
              if (!timeRemaining.isExpired) setIsTimeBoxHidden(!isTimeBoxHidden);
            }}
            onKeyDown={(event) => {
              if (!timeRemaining.isExpired && (event.key === 'Enter' || event.key === ' ')) {
                event.preventDefault();
                setIsTimeBoxHidden(!isTimeBoxHidden);
              }
            }}
            role={!timeRemaining.isExpired ? 'button' : undefined}
            tabIndex={!timeRemaining.isExpired ? 0 : undefined}
            aria-live={timeRemaining.isExpired ? 'assertive' : 'polite'}
            aria-label={timeRemaining.isExpired
              ? 'Time expired. Your responses have been saved.'
              : `${timeRemaining.remainingSeconds} seconds remaining`}
            title={timeRemaining.isExpired
              ? 'Time expired'
              : isTimeBoxHidden ? "Click to show remaining time" : "Click to hide remaining time"}
          >
            <div className={cssStyles.timeRemainingContent}>
              <div className={cssStyles.timeRemainingLabel}>Time remaining</div>
              <div className={cssStyles.timeRemainingText}>
                {timeRemaining.isExpired
                  ? 'Time expired'
                  : isTimeBoxHidden
                    ? '--:--'
                    : timeRemaining.formattedTime
                }
              </div>
              <div className={cssStyles.timeRemainingHint}>
                {timeRemaining.isExpired
                  ? 'Responses saved'
                  : `Click to ${isTimeBoxHidden ? 'show' : 'hide'}`}
              </div>
            </div>
          </div>
        )}
      </div>

      {focusWarningVisible && (
        <div className={cssStyles.focusWarning} role="status" aria-live="polite">
          <img src="/images/icons/warning.svg" alt="" className={cssStyles.focusWarningIcon} />
          <div className={cssStyles.focusWarningText}>
            <strong>Keep this window in focus.</strong>
            <span>Your timer continued while the assessment window was inactive.</span>
          </div>
          <span className={cssStyles.focusWarningCount}>
            {focusLossCount} focus change{focusLossCount === 1 ? '' : 's'}
          </span>
          <button
            type="button"
            className={cssStyles.focusWarningDismiss}
            onClick={() => setFocusWarningVisible(false)}
            aria-label="Dismiss focus warning"
          >
            ×
          </button>
        </div>
      )}

      <div className={cn(cssStyles.progressBar, "questions-progress-bar")}>
        <div className={cssStyles.progressContainer}>
          {moduleVersion.questions.map((question, index) => {
            const selectedAnswers = pendingAnswers[question.id] ?? initialAnswers[question.id] ?? [];
            const isAnswered = selectedAnswers.length > 0 || Boolean(freeTextAnswers[question.id]?.trim());
            const isCurrent = index === currentQuestionIndex;
            const isSkipped = !isAnswered && index < currentQuestionIndex;
            const isClickable = !isCurrent && !timeRemaining?.isExpired;
            const segmentWidth = (1 / moduleVersion.questions.length) * 100;
            
            return (
              <div
                key={question.id}
                className={cn(
                  cssStyles.progressSegment,
                  isAnswered && cssStyles['progressSegment--answered'],
                  isSkipped && cssStyles['progressSegment--skipped'],
                  isCurrent && cssStyles['progressSegment--current'],
                  isClickable && cssStyles['progressSegment--clickable']
                )}
                style={{ width: `${segmentWidth}%` }}
                onClick={isClickable ? () => handleGoToQuestion(index + 1) : undefined}
                onKeyDown={isClickable ? (event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    void handleGoToQuestion(index + 1);
                  }
                } : undefined}
                role={isClickable ? 'button' : undefined}
                tabIndex={isClickable ? 0 : undefined}
                aria-current={isCurrent ? 'step' : undefined}
                aria-label={`Question ${index + 1}${isCurrent ? ', current' : isAnswered ? ', answered' : ', unanswered'}`}
                title={isClickable ? `Go to Question ${index + 1}${isSkipped ? ' (Skipped)' : ''}` : undefined}
              />
            );
          })}
        </div>
        <div className={cssStyles.progressInfo}>
          <span className={cssStyles.progressText}>
            Question {currentQuestionIndex + 1} of {moduleVersion.questions.length}
          </span>
          <span className={cssStyles.progressAnswered}>
            {answeredQuestionCount} answered
          </span>
        </div>
        <div className={cssStyles.progressLegend} aria-label="Question progress legend">
          <span><i className={cn(cssStyles.legendSwatch, cssStyles['legendSwatch--answered'])} /> Answered</span>
          <span><i className={cn(cssStyles.legendSwatch, cssStyles['legendSwatch--skipped'])} /> Skipped</span>
          <span><i className={cn(cssStyles.legendSwatch, cssStyles['legendSwatch--current'])} /> Current</span>
        </div>
      </div>

      <div className={cn(cssStyles.questionContainer, "questions-question-container")}>
        {timeRemaining?.isExpired && (
          <div className={cssStyles.expiredOverlay}>
            <div className={cssStyles.expiredMessage}>
              <div className={cssStyles.expiredIcon}><img src="/images/icons/time.svg" alt="Time expired" style={{width: '48px', height: '48px'}} /></div>
              <div className={cssStyles.expiredText}>Time Expired</div>
            </div>
          </div>
        )}
        
        <div className={cn(cssStyles.questionHeader, "questions-question-header")}>
          <div className={cssStyles.questionHeaderTop}>
            <h3>Question {currentQuestionIndex + 1}</h3>
            <div className={cssStyles.questionType}>
              {formatQuestionType(currentQuestion.type)}
            </div>
          </div>
        </div>

        {/* Separator after question header */}
        <div className={cssStyles.questionHeaderSeparator}></div>

        {currentQuestion.text && (
          <div className={cn(cssStyles.questionText, "questions-question-text")}>
            {currentQuestion.text}
          </div>
        )}

        {currentQuestion.staticFileUrls && currentQuestion.staticFileUrls.length > 0 && (
          <div className={cn(cssStyles.questionAttachments, "questions-question-attachments")}>
            <FilePreview 
              files={convertUrlsToStaticFiles(currentQuestion.staticFileUrls)} 
              readonly={true}
              title=""
              showFileName={false}
              customStyles={{
                filesGrid: {
                  display: 'grid',
                  gridTemplateColumns: currentQuestion.staticFileUrls.length > 1 
                    ? 'repeat(auto-fit, minmax(min(300px, 100%), 1fr))' 
                    : '1fr',
                  gap: '8px',
                  paddingTop: '8px',
                  maxWidth: '100%'
                }
              }}
            />
          </div>
        )}

        {/* Separator between question and answers */}
        <div className={cssStyles.questionAnswersSeparator}></div>

        <div 
          className={cn(
            cssStyles.answersContainer,
            "questions-answers-container",
            isVisualAnswerSet && cssStyles.visualAnswers,
            isVisualAnswerSet && "questions-grid-layout"
          )}
        >
          {parseQuestionType(currentQuestion.type) === QuestionType.FreeText ? (
            <div className={cssStyles.freeTextInputContainer}>
              <textarea
                placeholder="Enter your answer here..."
                className={cssStyles.freeTextInput}
                disabled={timeRemaining?.isExpired}
                value={freeTextAnswers[currentQuestion.id] || ''}
                onChange={(e) => handleFreeTextChange(currentQuestion.id, e.target.value)}
              />
            </div>
          ) : (
            currentQuestion.answers.map((answer: ExamTakerPossibleAnswer, index: number) => (
              <div 
                key={answer.id} 
                className={cn(
                  cssStyles.answerOption,
                  "questions-answer-option",
                  isAnswerSelected(answer.id) && cssStyles.selected,
                  timeRemaining?.isExpired && cssStyles.disabled
                )}
                onClick={() => {
                  if (!timeRemaining?.isExpired) {
                    handleAnswerSelection(answer.id, currentQuestion.id, parseQuestionType(currentQuestion.type));
                  }
                }}
              >
                <div className={cssStyles.answerSelector}>
                  <input
                    type={parseQuestionType(currentQuestion.type) === QuestionType.MultipleChoice ? "checkbox" : "radio"}
                    id={`answer-${answer.id}`}
                    name={`question-${currentQuestion.id}`}
                    value={answer.id}
                    className={cn(cssStyles.answerRadio)}
                    checked={isAnswerSelected(answer.id)}
                    disabled={timeRemaining?.isExpired}
                    aria-label={`Answer ${String.fromCharCode(65 + index)}`}
                    onChange={() => {
                      if (!timeRemaining?.isExpired) {
                        handleAnswerSelection(answer.id, currentQuestion.id, parseQuestionType(currentQuestion.type));
                      }
                    }}
                  />
                  <span 
                    className={cssStyles.answerLetter}
                  >
                    {String.fromCharCode(65 + index)}
                  </span>
                </div>
                <label 
                  htmlFor={`answer-${answer.id}`} 
                  className={cn(
                    cssStyles.answerLabel
                  )}
                  onClick={(e) => e.preventDefault()} // Prevent label from triggering the input
                >
                  <div className={cssStyles.answerContent}>
                    {shouldShowAnswerText(answer, isVisualAnswerSet) && <div>{answer.text}</div>}
                    {answer.staticFileUrls && answer.staticFileUrls.length > 0 && (
                      <div 
                        className={cssStyles.answerAttachments}
                        onClick={(e) => e.stopPropagation()} // Prevent answer selection when clicking on attachments
                      >
                        <FilePreview 
                          files={convertUrlsToStaticFiles(answer.staticFileUrls)} 
                          readonly={true}
                          title=""
                          showFileName={false}
                          customStyles={{
                            container: {
                              width: '100%',
                              maxWidth: '100%'
                            },
                              filesGrid: {
                                display: 'grid',
                                gridTemplateColumns:
                                  // If all answers have attachments (grid mode), keep it simple
                                isVisualAnswerSet
                                  ? '1fr'
                                  : answer.staticFileUrls.length > 1 
                                    ? 'repeat(auto-fit, minmax(180px, 1fr))' 
                                    : '1fr',
                              gap: '4px',
                              paddingTop: '4px'
                            },
                            fileCard: {
                              width: '100%',
                              marginTop: '0px'
                            }
                          }}
                        />
                      </div>
                    )}
                  </div>
                </label>
              </div>
            ))
          )}
        </div>
      </div>

      <div className={cn(cssStyles.navigation, "questions-navigation")}>
        <div className={cn(cssStyles.navigationControls, "questions-navigation-controls")}>
          <div className={cn(cssStyles.mainButtons, "questions-main-buttons")}>
            {currentQuestionIndex > 0 ? (
              <button
                onClick={handlePreviousQuestion}
                disabled={timeRemaining?.isExpired}
                className={cn(
                  timeRemaining?.isExpired 
                    ? cssStyles.navigationButtonDisabled 
                    : cssStyles.navigationButton,
                  "questions-navigation-button"
                )}
              >
                ← Previous
              </button>
            ) : (
              <button 
                className={cn(cssStyles.navigationButtonDisabled, "questions-navigation-button")}
                disabled
              >
                ← Previous
              </button>
            )}
            
            <button
              onClick={() => setShowGoToModal(true)}
              disabled={timeRemaining?.isExpired}
              className={cn(
                timeRemaining?.isExpired 
                  ? cssStyles.navigationButtonDisabled 
                  : cssStyles.goToButton,
                "questions-navigation-button"
              )}
              title="Jump to question"
            >
              Go to Q#
            </button>
            
            {currentQuestionIndex < moduleVersion.questions.length - 1 ? (
              <button
                onClick={handleNextQuestion}
                disabled={timeRemaining?.isExpired}
                className={cn(
                  timeRemaining?.isExpired 
                    ? cssStyles.navigationButtonDisabled 
                    : cssStyles.navigationButton,
                  "questions-navigation-button"
                )}
              >
                Next →
              </button>
            ) : (
              <button
                onClick={handleSubmitModule}
                className={cn(
                  timeRemaining?.isExpired 
                    ? cssStyles.navigationButtonDisabled 
                    : cssStyles.submitButton,
                  "questions-submit-button"
                )}
                disabled={isSubmitting || timeRemaining?.isExpired}
              >
                {isSubmitting ? 'Submitting...' : 'Submit'}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Go to Q# Modal */}
      {showGoToModal && (
        <div className={cssStyles.modalOverlay} onClick={() => setShowGoToModal(false)}>
          <div className={cn(cssStyles.modal, cssStyles.goToQuestionModal, "questions-modal")} onClick={(e) => e.stopPropagation()}>
            <div className={cssStyles.modalHeader}>
              <h3 className={cssStyles.modalTitle}>Go to Question</h3>
              <button
                onClick={() => setShowGoToModal(false)}
                className={cssStyles.modalCloseButton}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <div className={cssStyles.modalBody}>
              <div className={cn(
                cssStyles.goToContainer,
                "go-to-container",
                showShakeAnimation && cssStyles.shakeAnimation
              )}>
                <label className={cssStyles.goToLabel}>Enter question number (1-{moduleVersion.questions.length}):</label>
                <input
                  type="text"
                  value={goToInput}
                  onChange={handleGoToInputChange}
                  onKeyPress={handleGoToKeyPress}
                  placeholder={`1 - ${moduleVersion.questions.length}`}
                  className={cn(cssStyles.goToInput, "questions-go-to-input")}
                  autoFocus
                />
                {showErrorPopup && (
                  <div className={cssStyles.errorMessage} style={{ color: '#d32f2f', marginTop: '8px', fontSize: '14px' }}>
                    <img src="/images/icons/warning.svg" alt="Warning" style={{width: '16px', height: '16px', marginRight: '6px', verticalAlign: 'middle', display: 'inline-block'}} /> Please enter a number between 1 and {moduleVersion.questions.length}
                  </div>
                )}
              </div>
            </div>
            <div className={cssStyles.modalFooter}>
              <button
                onClick={() => setShowGoToModal(false)}
                className={cssStyles.cancelButton}
              >
                Cancel
              </button>
              <button
                onClick={handleGoToSubmit}
                disabled={!goToInput || isNaN(parseInt(goToInput)) || parseInt(goToInput) < 1 || parseInt(goToInput) > moduleVersion.questions.length}
                className={cn(
                  !goToInput || isNaN(parseInt(goToInput)) || parseInt(goToInput) < 1 || parseInt(goToInput) > moduleVersion.questions.length
                    ? cssStyles.goToButtonDisabled
                    : cssStyles.navigationButton,
                  "questions-go-to-button"
                )}
              >
                Go to Question
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      {showConfirmationModal && (
        <div className={cssStyles.modalOverlay}>
          <div className={cn(cssStyles.modal, cssStyles.modalLarge, "questions-modal")}>
            <div className={cssStyles.modalHeader}>
              <h3 className={cssStyles.modalTitle}>Confirm Module Submission</h3>
            </div>
            <div className={cssStyles.modalBody}>
              {(() => {
                const unansweredQuestions = getUnansweredQuestions();
                const hasUnanswered = unansweredQuestions.length > 0;
                
                return (
                  <>
                    <div className={cssStyles.warningIcon}>
                      {hasUnanswered ? 
                        <img src="/images/icons/warning.svg" alt="Warning" style={{width: '32px', height: '32px'}} /> : 
                        <img src="/images/icons/check.svg" alt="Check" style={{width: '32px', height: '32px'}} />
                      }
                    </div>
                    <div className={cssStyles.modalContent}>
                      {hasUnanswered ? (
                        <>
                          <p className={cssStyles.modalText}>
                            <strong>Warning:</strong> You have {unansweredQuestions.length} unanswered question{unansweredQuestions.length > 1 ? 's' : ''}:
                          </p>
                          <div className={cssStyles.unansweredList}>
                            {unansweredQuestions.map((question, idx) => {
                              const questionIndex = moduleVersion?.questions.findIndex(q => q.id === question.id);
                              return (
                                <div 
                                  key={question.id} 
                                  className={cssStyles.unansweredItem}
                                  onClick={() => {
                                    setShowConfirmationModal(false);
                                    if (questionIndex !== undefined && questionIndex >= 0) {
                                      handleGoToQuestion(questionIndex + 1);
                                    }
                                  }}
                                >
                                  <span className={cssStyles.unansweredNumber}>Question {questionIndex !== undefined ? questionIndex + 1 : '?'}</span>
                                  <span className={cssStyles.unansweredIcon}>→</span>
                                </div>
                              );
                            })}
                          </div>
                          <p className={cssStyles.modalText}>
                            Click on a question above to go back and answer it, or proceed to submit with unanswered questions.
                          </p>
                        </>
                      ) : (
                        <p className={cssStyles.modalText}>
                          All questions have been answered.
                        </p>
                      )}
                      <p className={cssStyles.modalText}>
                        <strong>Important:</strong> Once you submit this module, you will not be able to change any of your answers.
                      </p>
                      <p className={cssStyles.modalText}>
                        Are you sure you want to submit your responses?
                      </p>
                    </div>
                  </>
                );
              })()}
            </div>
            <div className={cssStyles.modalFooter}>
              <button
                onClick={handleCancelSubmit}
                className={cssStyles.cancelButton}
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmSubmit}
                className={cn(
                  cssStyles.confirmButton,
                  isSubmitting && "opacity-70 cursor-not-allowed"
                )}
                style={isSubmitting ? { opacity: 0.7, cursor: 'not-allowed' } : {}}
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Submitting...' : 'Confirm Submission'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Time Expired Modal */}
      {showTimeExpiredModal && (
        <div 
          className={cssStyles.modalOverlay}
          onClick={(e) => e.stopPropagation()} // Prevent closing on overlay click
        >
          <div className={cn(cssStyles.modal, "questions-modal")}>
            <div className={cssStyles.modalBody}>
              <div className={cssStyles.timeExpiredIcon}><img src="/images/icons/time.svg" alt="Time expired" style={{width: '48px', height: '48px'}} /></div>
              <div className={cssStyles.modalContent}>
                <p className={cssStyles.modalText}>
                  <strong>Time has run out!</strong>
                </p>
                <p className={cssStyles.modalText}>
                  The time limit for this module has been reached. Your current answers have been automatically saved.
                </p>
                {moduleProgress?.startedAtUtc && (
                  <p className={cssStyles.modalText}>
                    Module started: {formatDateToLocal(moduleProgress.startedAtUtc)}
                  </p>
                )}
                <p className={cssStyles.modalText}>
                  Click the button below to return to the assignment page.
                </p>
              </div>
            </div>
            <div className={cssStyles.modalFooter}>
              <button
                onClick={handleTimeExpired}
                className={cssStyles.timeExpiredButton}
              >
                Back to Assignment
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Questions;
